import React, { useCallback, useMemo, useState } from 'react';

import dayjs from '~/timeInit';

import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';

import { toSortableTimestamp } from '~/services/TimeService';

import getErrorMessage from '~/utils/getErrorMessage';

import TabPanelShell from '../../../shared/TabPanelShell';
import { useDealInformationTabCount } from '../../DealInformationTabCounts';
import { useFilteredByExpiry } from '../../shared/useFilteredByExpiry';
import { useTableSort } from '../../shared/useTableSort';
import { UNMATCHED_ROW_ID, toggleExpandedRow } from '../surcharges/surchargeTabLayout';
import type { RoomRateSelection } from '../surcharges/surchargeUtils';

import BlackoutTableHeader, { BlackoutSortField } from './BlackoutTableHeader';
import SalesforceBlackoutRow from './SalesforceBlackoutRow';
import UnmatchedBlackoutSection from './UnmatchedBlackoutSection';
import {
  buildAppliedRoomsByBlackoutId,
  buildRoomRateBlackoutAssignments,
  getMissingRoomTypesForBlackout,
  getUnmatchedRoomRateBlackoutAssignments,
  isSalesforceBlackoutDateExpired,
} from './appliedBlackoutUtils';
import { classifyDaysScope, classifyRoomsScope, formatDaysScope, formatRoomsScope } from './blackoutScopeClassifier';
import type { SalesforceBlackoutDate } from './types';

interface BlackoutScopeRow {
  blackoutDate: SalesforceBlackoutDate;
  days: ReturnType<typeof classifyDaysScope>;
  rooms: ReturnType<typeof classifyRoomsScope>;
}

const BLACKOUT_TABLE_COLUMN_COUNT = 5;

interface Props {
  blackoutDates: Array<SalesforceBlackoutDate>;
  blackoutDatesByRoomRate: Record<string, Array<App.BlackoutDate>>;
  blackoutDatesByRoomRateError?: unknown;
  canApplyBlackoutDates?: boolean;
  error?: unknown;
  isLoading: boolean;
  isLoadingBlackoutDatesByRoomRate: boolean;
  onImportSalesforceBlackoutDates?: () => void;
  onUploadStopsellDocument?: () => void;
  propertyId?: string;
  roomRateSelections: Array<RoomRateSelection>;
  roomTypeNames: Array<string>;
  vendorId?: string;
}

export default function BlackoutDatesTab({
  blackoutDates,
  blackoutDatesByRoomRate,
  blackoutDatesByRoomRateError,
  canApplyBlackoutDates,
  error,
  isLoading,
  isLoadingBlackoutDatesByRoomRate,
  onImportSalesforceBlackoutDates,
  onUploadStopsellDocument,
  propertyId,
  roomRateSelections,
  roomTypeNames,
  vendorId,
}: Props) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [hideExpired, setHideExpired] = useState(true);
  useDealInformationTabCount('blackout-dates', isLoading || error ? undefined : blackoutDates.length);
  const now = dayjs.tz();

  const isExpired = useCallback(
    (blackoutDate: SalesforceBlackoutDate) => isSalesforceBlackoutDateExpired(blackoutDate, now),
    [now],
  );
  const visibleBlackoutDates = useFilteredByExpiry(blackoutDates, isExpired, hideExpired);
  const scopedBlackoutRows = useMemo(
    () =>
      visibleBlackoutDates.map((blackoutDate) => ({
        blackoutDate,
        days: classifyDaysScope(blackoutDate.name),
        rooms: classifyRoomsScope(blackoutDate.name, roomTypeNames),
      })),
    [visibleBlackoutDates, roomTypeNames],
  );
  const getBlackoutSortValue = useCallback((row: BlackoutScopeRow, field: BlackoutSortField) => {
    switch (field) {
      case 'name':
        return row.blackoutDate.name;
      case 'from__c':
        return toSortableTimestamp(row.blackoutDate.from__c);
      case 'rooms':
        return formatRoomsScope(row.rooms);
      case 'days':
        return formatDaysScope(row.days);
      default:
        return null;
    }
  }, []);
  const {
    sortField,
    sortDirection,
    handleSortChange,
    sortedRows: salesforceRows,
  } = useTableSort(scopedBlackoutRows, getBlackoutSortValue, 'from__c');

  const roomRateBlackoutAssignments = useMemo(
    () =>
      buildRoomRateBlackoutAssignments({
        blackoutDates,
        blackoutDatesByRoomRate,
        propertyId,
        roomRateSelections,
        roomTypeNames,
        vendorId,
      }),
    [blackoutDates, blackoutDatesByRoomRate, propertyId, roomRateSelections, roomTypeNames, vendorId],
  );

  const appliedRoomsByBlackoutId = useMemo(
    () => buildAppliedRoomsByBlackoutId(roomRateBlackoutAssignments),
    [roomRateBlackoutAssignments],
  );

  const unmatchedRoomRateBlackoutAssignments = useMemo(
    () => getUnmatchedRoomRateBlackoutAssignments(roomRateBlackoutAssignments),
    [roomRateBlackoutAssignments],
  );

  if (isLoading) {
    return (
      <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
        <CircularProgress />
        <Typography color="text.secondary">Loading blackout dates.</Typography>
      </Stack>
    );
  }

  if (error) {
    return <Alert severity="error">{getErrorMessage(error, 'Unable to load Salesforce blackout dates.')}</Alert>;
  }

  return (
    <TabPanelShell
      title="Blackout Dates"
      contentSx={{ p: 0 }}
      actions={
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
          <Button
            disabled={!canApplyBlackoutDates}
            onClick={onImportSalesforceBlackoutDates}
            startIcon={<AddIcon />}
            variant="contained"
          >
            Import from Salesforce
          </Button>
          <Button
            disabled={!canApplyBlackoutDates}
            onClick={onUploadStopsellDocument}
            startIcon={<UploadFileIcon />}
            variant="text"
          >
            Upload stopsell document
          </Button>
        </Stack>
      }
      titleActions={
        <Button
          size="small"
          variant="outlined"
          startIcon={hideExpired ? <VisibilityIcon /> : <VisibilityOffIcon />}
          onClick={() => setHideExpired((current) => !current)}
          sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
        >
          {hideExpired ? 'Show expired blocks' : 'Hide expired blocks'}
        </Button>
      }
    >
      <Stack spacing={0.5} sx={{ px: 3, pt: 3, pb: 1.5 }}>
        <Typography color="text.secondary" variant="body2">
          Expand a Salesforce blackout date to see the room rates with a matching blackout block.
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 3 }}>
          <Typography color="text.secondary" component="li" variant="body2">
            Room types shown as{' '}
            <Box component="span" sx={{ color: 'warning.dark', fontWeight: 700 }}>
              Missing
            </Box>{' '}
            exist in Salesforce but have no matching block in our system yet — use &quot;Import from Salesforce&quot;
            above to create them.
          </Typography>
          <Typography color="text.secondary" component="li" variant="body2">
            Room-rate blackout blocks with no matching Salesforce blackout were likely added manually post-launch at
            hotel request or extracted from a stopsell document upload.
          </Typography>
        </Box>
        <Typography color="text.secondary" variant="caption">
          * Rooms and Days are attempted extractions from the blackout Name text and may be incorrect — verify in
          Salesforce if unsure.
        </Typography>
      </Stack>
      <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
        <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
          <colgroup>
            <col style={{ width: 48 }} />
            <col />
            <col style={{ width: 220 }} />
            <col style={{ width: 210 }} />
            <col style={{ width: 200 }} />
          </colgroup>
          <BlackoutTableHeader
            columnCount={BLACKOUT_TABLE_COLUMN_COUNT}
            sortField={sortField}
            sortDirection={sortDirection}
            onSortChange={handleSortChange}
          />
          <TableBody>
            {blackoutDates.length === 0 && (
              <TableRow>
                <TableCell colSpan={BLACKOUT_TABLE_COLUMN_COUNT}>
                  <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                    No Salesforce blackout dates found for this opportunity.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {blackoutDates.length > 0 && salesforceRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={BLACKOUT_TABLE_COLUMN_COUNT}>
                  <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                    All Salesforce blackout dates for this opportunity are expired.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {salesforceRows.map(({ blackoutDate, days, rooms }) => {
              const blackoutId = blackoutDate.sfid;
              const matchingAssignments = appliedRoomsByBlackoutId[blackoutId] ?? [];
              const missingRoomTypes =
                isLoadingBlackoutDatesByRoomRate || blackoutDatesByRoomRateError
                  ? []
                  : getMissingRoomTypesForBlackout(
                      blackoutDate,
                      matchingAssignments,
                      roomRateSelections,
                      roomTypeNames,
                    );

              return (
                <SalesforceBlackoutRow
                  key={blackoutId}
                  blackoutDate={blackoutDate}
                  blackoutDatesByRoomRateError={blackoutDatesByRoomRateError}
                  columnCount={BLACKOUT_TABLE_COLUMN_COUNT}
                  days={days}
                  hideExpired={hideExpired}
                  isExpanded={expandedRows.has(blackoutId)}
                  matchingAssignments={matchingAssignments}
                  missingRoomTypes={missingRoomTypes}
                  now={now}
                  rooms={rooms}
                  onToggleExpanded={() => setExpandedRows((current) => toggleExpandedRow(current, blackoutId))}
                />
              );
            })}
            <UnmatchedBlackoutSection
              assignments={unmatchedRoomRateBlackoutAssignments}
              columnCount={BLACKOUT_TABLE_COLUMN_COUNT}
              error={blackoutDatesByRoomRateError}
              hideExpired={hideExpired}
              isExpanded={expandedRows.has(UNMATCHED_ROW_ID)}
              isLoading={isLoadingBlackoutDatesByRoomRate}
              now={now}
              onToggleExpanded={() => setExpandedRows((current) => toggleExpandedRow(current, UNMATCHED_ROW_ID))}
            />
          </TableBody>
        </Table>
      </TableContainer>
    </TabPanelShell>
  );
}
